import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import Player from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import type { eventWithTime } from '@rrweb/types';
import type { AgentAction, Device, VisitStatus } from '@daevio/shared';
import { deviceLabel, formatClock } from '../lib/format.ts';
import { actionLabel, visitStatusLabel } from '../lib/synthetic.ts';
import { LiveStage, type LiveLoader } from './LiveStage.tsx';
import { Avatar, Pill, type PillTone } from './ui.tsx';

const TONE: Record<VisitStatus, PillTone> = {
  queued: 'grey',
  running: 'blue',
  converted: 'green',
  satisfied: 'green',
  abandoned: 'rose',
  interrupted: 'amber',
  failed: 'rose',
  blocked: 'amber',
  login_wall: 'amber',
  cancelled: 'grey',
};

const OPEN = new Set(['queued', 'running']);
/** Visits that ended without reaching their goal: the wall names their status instead of the mood. */
const GAVE_UP = new Set(['abandoned', 'failed', 'blocked']);

/** What a tile needs to know about a visit: the public summary and the dashboard's both provide it. */
export interface TileVisit {
  index: number;
  personaName: string;
  device: Device;
  status: VisitStatus;
  stepCount: number;
  durationMs: number | null;
  finalMood: number | null;
  /** latest thought, shown while the visit runs or when it recorded nothing */
  lastThought?: string | null;
  intent: string;
}

export interface TileStep {
  index: number;
  ts: string;
  thought: string;
  mood: number;
  action: AgentAction;
}

/** Mounts children only once the element has come within one screen of the viewport. */
function useInView(ref: React.RefObject<HTMLElement | null>, margin = '100%') {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: margin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, margin, near]);
  return near;
}

/** Runs `load` once `wanted` turns true; the loader's identity may change between renders. */
function useLazy<T>(wanted: boolean, load: () => Promise<T>): T | undefined {
  const [data, setData] = useState<T | undefined>(undefined);
  const loader = useRef(load);
  loader.current = load;
  const started = useRef(false);
  useEffect(() => {
    if (!wanted || started.current) return;
    started.current = true;
    let alive = true;
    loader
      .current()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [wanted]);
  return data;
}

/**
 * The colour of a visitor. The ink design tells visitors apart by their initials and name, not by hue: every
 * visitor is ink, and the rose is kept for where something went wrong.
 */
export const VISITOR_COLORS = ['#0e1526'];
export function visitorColor(index: number): string {
  return VISITOR_COLORS[index % VISITOR_COLORS.length]!;
}

interface Props {
  visit: TileVisit;
  /** where the tile leads: the visit's replay */
  href: string;
  loadSteps: () => Promise<TileStep[]>;
  loadRecording: () => Promise<eventWithTime[]>;
  /** Loads the recording while the visit runs: the tile then shows it live. */
  loadLive?: LiveLoader;
  /**
   * `card`: the recording with its caption and the visitor below; `thumb`: a small frame paused at `step`;
   * `wall`: a clean frame with the flagged moments on a track below it; `feature`: the same, large, with the
   * thought of the moment under it.
   */
  variant?: 'card' | 'thumb' | 'wall' | 'feature';
  /** Where the visitor hit a problem, as fractions of the visit (0 to 1), drawn on the track of `wall` and `feature`. */
  marks?: number[];
  /** Pause at this step instead of playing (the moment of an issue). */
  step?: number;
  /** Text under a thumb (the moment), instead of the visitor block. */
  label?: string;
  /** Accessible name of the link (a thumb's text is the moment, not what the link does). */
  ariaLabel?: string;
}

/**
 * A recording of one visit, playing muted on a loop with the visitor's current thought as a subtitle:
 * the proof that a real browser opened the site. The player mounts only near the viewport; before the
 * visit ends (or when it recorded nothing) the tile shows the latest thought instead.
 */
export function RecordingTile({ visit, href, loadSteps, loadRecording, loadLive, variant = 'card', step, label, ariaLabel, marks }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref);
  const open = OPEN.has(visit.status);
  const wanted = near && !open && visit.stepCount > 0;
  // Thumbs never follow a running visit: the card or wall tile of the same visit already polls it.
  const watching = near && variant !== 'thumb' && visit.status === 'running' && !!loadLive;
  // The caption over the frame belongs to the card and the thumb; the wall keeps the frame clean.
  const overlay = variant === 'card' || variant === 'thumb';
  const steps = useLazy(wanted, loadSteps) ?? [];
  const recording = useLazy(wanted, loadRecording);
  const color = visitorColor(visit.index);

  const events = useMemo(() => (recording ? [...recording].sort((a, b) => a.timestamp - b.timestamp) : null), [recording]);
  const playable = !!events && events.length >= 2 && events.some((e) => e.type === 2);
  const startTs = events?.[0]?.timestamp ?? 0;
  const [time, setTime] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !events || !playable) return;
    const width = Math.max(60, stage.clientWidth);
    const height = Math.max(100, stage.clientHeight);
    let player: Player;
    try {
      player = new Player({
        target: stage,
        props: {
          events,
          width,
          height,
          autoPlay: step === undefined,
          showController: false,
          skipInactive: true,
          speed: 1,
          mouseTail: { strokeStyle: color, lineWidth: 2, duration: 700 },
          inactiveColor: '#e3e6ee',
        },
      });
    } catch {
      return;
    }
    player.addEventListener('ui-update-current-time', (detail) => setTime((detail as { payload: number }).payload));
    // A loop: the recording starts again when it ends.
    player.addEventListener('finish', () => {
      if (step === undefined) player.goto(0, true);
    });
    if (step !== undefined) {
      const at = steps.find((s) => s.index === step);
      if (at) player.goto(Math.max(0, Date.parse(at.ts) - startTs), false);
    }
    return () => {
      (player as unknown as { $destroy(): void }).$destroy();
      stage.innerHTML = '';
    };
    // steps/startTs are settled once the detail and the recording have both loaded.
  }, [events, playable, color, step, steps, startTs]);

  const current =
    step !== undefined
      ? steps.find((s) => s.index === step)
      : playable
        ? steps.findLast((s) => Date.parse(s.ts) - startTs <= time + 250)
        : undefined;
  const thought = current?.thought ?? visit.lastThought ?? steps[steps.length - 1]?.thought;
  const phone = visit.device !== 'desktop';

  return (
    <Link
      to={href}
      className={`rec-tile rec-tile-${variant}${phone ? ' rec-tile-phone' : ''}`}
      data-visit-status={visit.status}
      aria-label={ariaLabel ?? `Watch the recording of ${visit.personaName}`}
      style={{ '--visitor': color } as React.CSSProperties}
    >
      <div ref={ref} className="rec-frame">
        <div ref={stageRef} className="rec-stage daevio-block" aria-hidden="true" />
        {watching && loadLive && (
          <LiveStage
            load={loadLive}
            fit="contain"
            color={color}
            className="rec-stage"
            placeholder={
              <div className="rec-empty" aria-hidden="true">
                <span className="rec-empty-site" />
              </div>
            }
          />
        )}
        {!playable && !watching && (
          <div className="rec-empty" aria-hidden="true">
            <span className="rec-empty-site" />
          </div>
        )}
        <span className="rec-badge">
          <i className={open ? 'rec-live' : undefined} />
          {open ? (visit.status === 'running' ? 'Live' : visitStatusLabel[visit.status]) : playable ? formatClock(time) : visit.durationMs !== null ? formatClock(visit.durationMs) : '—'} ·{' '}
          {deviceLabel[visit.device].toLowerCase()}
        </span>
        {variant === 'card' && !open && (
          <span className="rec-play" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M7 4v16l13-8z" fill="currentColor" />
            </svg>
          </span>
        )}
        {thought && overlay && (
          <span className="rec-caption">
            {current && (
              <span className="rec-caption-meta mono">
                step {current.index + 1}: {actionLabel(current.action).toLowerCase()}, mood {current.mood}
              </span>
            )}
            <span className="rec-caption-text voice">“{thought}”</span>
          </span>
        )}
      </div>
      {(variant === 'wall' || variant === 'feature') && (
        <div className="rec-under">
          {marks && (
            <span className="rec-track" aria-label={`${marks.length} flagged ${marks.length === 1 ? 'moment' : 'moments'}`}>
              {marks.map((m, n) => (
                <i key={n} style={{ left: `${Math.min(1, Math.max(0, m)) * 100}%` }} />
              ))}
            </span>
          )}
          <span className="rec-who">
            <span className="rec-name">
              <Avatar index={visit.index} size="sm" />
              <span className="rec-name-text">
                {variant === 'feature' ? `Visitor ${visit.index + 1}: ${visit.personaName}` : visit.personaName}
              </span>
            </span>
            {open || GAVE_UP.has(visit.status) || visit.finalMood === null ? (
              <Pill tone={TONE[visit.status]}>{visitStatusLabel[visit.status]}</Pill>
            ) : (
              <span className="rec-mood mono">mood {visit.finalMood}/10</span>
            )}
          </span>
          {variant === 'feature' && thought && <span className="rec-feature-thought voice">“{thought}”</span>}
        </div>
      )}
      {variant === 'card' ? (
        <div className="rec-meta">
          <div className="rec-who">
            <span className="rec-name">
              <i style={{ background: color }} />
              {visit.personaName}
            </span>
            <span className={`status status-${visit.status}`}>{visitStatusLabel[visit.status]}</span>
          </div>
          <p className="rec-intent">Came to {visit.intent.charAt(0).toLowerCase() + visit.intent.slice(1)}</p>
          <p className="rec-facts mono">
            {visit.stepCount} {visit.stepCount === 1 ? 'step' : 'steps'}
            {visit.durationMs !== null ? `, ${formatClock(visit.durationMs)}` : ''}
            {visit.finalMood !== null ? ` · final mood ${visit.finalMood}/10` : ''}
          </p>
        </div>
      ) : variant === 'thumb' ? (
        <p className="rec-label">
          <i style={{ background: color }} />
          {label ?? visit.personaName}
        </p>
      ) : null}
    </Link>
  );
}
